/**
 * The dashboard's navigation, in one place for the desktop sidebar and the
 * mobile menu (they used to keep separate lists, and the mobile one had drifted:
 * no Links, Analytics or QR codes).
 *
 * Grouped for a new user: the everyday items stay visible, everything a first
 * week does not need folds under "More". Nothing is removed.
 */
import {
  BarChart3,
  Cable,
  Download,
  FileText,
  Globe,
  Image as ImageIcon,
  KeyRound,
  LayoutDashboard,
  Link2,
  Mail,
  MessageSquare,
  QrCode,
  Settings,
  Settings2,
  Users,
} from 'lucide-react';
import type { TenantRole } from '@/lib/permissions';

export type NavItem = {
  name: string;
  href: string;
  icon: typeof LayoutDashboard;
  /** Hidden below this role (the page itself says "no access" too). */
  minRole?: TenantRole;
};

export const PRIMARY_NAV: NavItem[] = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard },
  { name: 'Links', href: '/links', icon: Link2 },
  { name: 'Analytics', href: '/analytics', icon: BarChart3 },
  { name: 'Domains', href: '/domains', icon: Globe },
  { name: 'QR Codes', href: '/qr-codes', icon: QrCode },
];

export const MORE_NAV: NavItem[] = [
  { name: 'Mappings', href: '/mappings', icon: Settings2 },
  { name: 'Email Forwarding', href: '/email', icon: Mail },
  { name: 'Response Templates', href: '/templates', icon: FileText },
  { name: 'Gallery', href: '/gallery', icon: ImageIcon },
  { name: 'Connections', href: '/settings/connections', icon: Cable },
  { name: 'API Keys', href: '/settings/api-keys', icon: KeyRound },
  { name: 'Export', href: '/settings/export', icon: Download, minRole: 'admin' },
];

export const FOOTER_NAV: NavItem[] = [
  { name: 'Team', href: '/team', icon: Users },
  { name: 'Settings', href: '/settings', icon: Settings },
  { name: 'Support', href: '/tickets', icon: MessageSquare },
];

/** Exact match, or a sub-page — except /settings, whose sub-pages have their own items. */
export function isNavItemActive(pathname: string | null, href: string): boolean {
  if (!pathname) return false;
  if (pathname === href) return true;
  return href !== '/' && href !== '/settings' && pathname.startsWith(`${href}/`);
}

/** "More" opens by itself when the current page lives inside it. */
export function isInMore(pathname: string | null): boolean {
  return MORE_NAV.some((item) => isNavItemActive(pathname, item.href));
}

/** localStorage key for whether the user opened "More". */
export const MORE_OPEN_KEY = 'rii:sidebar-more-open';
